import '@theme/cart-drawer'; // ensures cart-drawer-component is registered first
import { morphSection } from '@theme/section-renderer';

/** @type {typeof import('./cart-drawer').CartDrawerComponent} */
const CartDrawerBase = customElements.get('cart-drawer-component');

// ---------------------------------------------------------------------------
// Cart recommendation analytics owner
// ---------------------------------------------------------------------------

const OCS_UPSELL_PROPERTY_KEYS = [
  '_item_id',
  '_item_category',
  '_item_category2',
  '_item_category3',
  '_item_brand',
  '_ontario_grown',
  '_craft',
  '_price_drop',
  '_new',
  '_item_list_name',
];

/**
 * Build raw add metadata from the current selected form control or modal option.
 * @param {{ dataset?: DOMStringMap }} source
 * @param {FormDataEntryValue|null} quantity
 * @returns {Record<string, unknown>}
 */
function getUpsellRawItem(source, quantity) {
  return {
    itemId: source?.dataset?.itemId,
    itemName: source?.dataset?.itemName,
    brand: source?.dataset?.itemBrand,
    itemVariant: source?.dataset?.itemVariant,
    category: source?.dataset?.itemCategory,
    category2: source?.dataset?.itemCategory2,
    category3: source?.dataset?.itemCategory3,
    ontarioGrown: source?.dataset?.ontarioGrown,
    craft: source?.dataset?.craft,
    priceCents: source?.dataset?.priceCents,
    compareAtCents: source?.dataset?.compareAtPriceCents,
    variantStatus: source?.dataset?.variantStatus,
    variantAvailable: source?.dataset?.variantAvailable,
    itemListName: source?.dataset?.itemListName,
    quantity,
  };
}

/**
 * Persist approved private properties and build the immutable request payload.
 * @param {FormData} formData
 * @param {{ dataset?: DOMStringMap }} source
 * @returns {{ api: any, payload: any }}
 */
function prepareUpsellAddAnalytics(formData, source) {
  const api = /** @type {any} */ (window).OCSAnalyticsEvents;
  if (
    !api ||
    typeof api.setCartLineProperties !== 'function' ||
    typeof api.createAddToCartPayload !== 'function' ||
    typeof api.publishOcsAnalyticsEvent !== 'function'
  ) {
    return { api: null, payload: null };
  }

  const rawItem = getUpsellRawItem(source, formData.get('quantity'));
  const listIsValid = rawItem.itemListName === 'You may also like';
  const persistenceItem = listIsValid ? rawItem : { ...rawItem, itemListName: undefined };
  try {
    if (typeof formData.delete === 'function') {
      for (const key of OCS_UPSELL_PROPERTY_KEYS) {
        formData.delete(`properties[${key}]`);
      }
    }
    api.setCartLineProperties(formData, persistenceItem);
    const payload = api.createAddToCartPayload([rawItem], 'Cart Flyout');
    return { api, payload: listIsValid ? payload : null };
  } catch (_) {
    return { api, payload: null };
  }
}

/**
 * Publish one successful cart recommendation add.
 * @param {{ api: any, payload: any }} analytics
 */
function publishUpsellAddAnalytics(analytics) {
  if (!analytics.api || !analytics.payload) return;
  try {
    analytics.api.publishOcsAnalyticsEvent('ocs:add_to_cart', analytics.payload);
  } catch (_) {}
}

/**
 * Read the open drawer's lines for the view_cart payload.
 *
 * Production read the rendered flyout the same way — `.cart__item` rows, the
 * gram-limit value, the subtotal — because that is what the shopper is looking
 * at. Here the numbers come from the server-rendered dataset on each row
 * (snippets/ocs-cart-item.liquid) rather than from parsed money strings, and
 * from `data-ocs-total-cents` on the summary, so no locale or currency format
 * can change the value.
 *
 * @param {ParentNode} root
 * @returns {{rawItems: object[], driedCannabisEquiv: string, totalCents: string|undefined}}
 */
function readCartViewSnapshot(root) {
  const rows = root?.querySelectorAll?.('[data-ocs-item-name]') ?? [];
  const rawItems = [];

  for (const row of rows) {
    const dataset = /** @type {HTMLElement} */ (row).dataset;
    if (!dataset) continue;

    rawItems.push({
      itemId: dataset.ocsPropertyItemId,
      itemName: dataset.ocsItemName,
      priceCents: dataset.ocsPriceCents,
      quantity: dataset.ocsQuantity,
    });
  }

  const grams = root?.querySelector?.('.js-ocs-current-grams');
  const totals = root?.querySelector?.('[data-ocs-total-cents]');

  return {
    rawItems,
    driedCannabisEquiv: grams?.textContent ?? '',
    totalCents: /** @type {HTMLElement|null} */ (totals)?.dataset?.ocsTotalCents,
  };
}

/**
 * Build locale-aware storefront URLs for Ajax cart and checkout paths.
 * @param {string} path
 * @returns {string}
 */
function getLocalizedStorefrontPath(path) {
  const root = window.Shopify?.routes?.root || '/';
  const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
  return `${normalizedRoot}${path.replace(/^\/+/, '')}`;
}

/**
 * Cart-related section IDs that need to be re-rendered after an upsell add.
 * @returns {string[]}
 */
function getCartSectionIds() {
  const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);

  return [...new Set(sectionIds)];
}

/**
 * Fetch freshly-rendered section HTML after Ajax add succeeds. Chrome can render
 * stale drawer HTML from the add.js sections payload in this flow, so force a
 * follow-up Section Rendering API read before dispatching cart:update.
 * @param {string[]} sectionIds
 * @param {Record<string, string>} [fallbackSections]
 * @returns {Promise<Record<string, string>>}
 */
async function fetchFreshCartSections(sectionIds, fallbackSections = {}) {
  if (!sectionIds.length) return fallbackSections;

  try {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionIds.join(','));
    const response = await fetch(url.toString(), { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) return fallbackSections;

    const freshSections = await response.json();
    const sections = { ...fallbackSections };
    for (const sectionId of sectionIds) {
      if (typeof freshSections?.[sectionId] === 'string') {
        sections[sectionId] = freshSections[sectionId];
      }
    }
    return sections;
  } catch (error) {
    console.error('[OCS] Fresh cart section render failed:', error);
    return fallbackSections;
  }
}

/**
 * @param {FormData} formData
 * @returns {number}
 */
function getFormDataQuantity(formData) {
  return Number(formData.get('quantity')) || 1;
}

// ---------------------------------------------------------------------------

/**
 * OCS-compliant cart drawer component.
 *
 * Extends the base CartDrawerComponent with cannabis compliance features:
 * - 30g dried cannabis equivalency limit enforcement
 * - Terms & conditions acceptance requirement
 * - Terms state persisted as cart attribute `va` (survives morphSection)
 * - `va: true` and displayed `dried_cannabis_equiv` cart attributes written before checkout redirect
 * - Checkout button enabled only when: terms accepted + under limit + cart not empty
 * - Upsell AJAX submit with GA4 line-item property forwarding
 * - Multi-variant upsell picker: variant selection updates hidden input + button
 *
 * Uses event delegation on the stable `dialog` ref so terms/checkout
 * state survives `morphSection` DOM replacements.
 */
class OcsCartDrawerComponent extends CartDrawerBase {
  #openingTransitionTimer = 0;
  #drawerRefreshToken = 0;
  #initialFocusFrame = 0;

  showDialog() {
    // Every path into the drawer funnels through here: CartDrawerBase.open()
    // calls showDialog(), and so does DialogComponent's own click handling. The
    // guard is for a showDialog() on an already-open drawer, which must not
    // count as a second view.
    const wasOpen = this.refs.dialog?.open === true;

    this.#prepareDialogForOpen();
    super.showDialog();
    this.#focusDialogShellAfterOpen();
    this.#refreshDrawerOnOpen();

    if (!wasOpen) this.#publishViewCart();
  }

  /**
   * Publish ocs:view_cart for this drawer open.
   *
   * Shopify's own `cart_viewed` fires only on a /cart page view, so a drawer
   * that never navigates produced nothing at all — defect G13. The pixel maps
   * this to the legacy `view_cart` push that GA4 tag 414 already listens for.
   */
  #publishViewCart() {
    const api = /** @type {any} */ (window).OCSAnalyticsEvents;
    if (
      !api ||
      typeof api.createViewCartPayload !== 'function' ||
      typeof api.publishOcsAnalyticsEvent !== 'function'
    ) {
      return;
    }

    try {
      const snapshot = readCartViewSnapshot(this);
      const payload = api.createViewCartPayload(snapshot.rawItems, {
        driedCannabisEquiv: snapshot.driedCannabisEquiv,
        totalCents: snapshot.totalCents,
      });
      if (!payload) return;

      api.publishOcsAnalyticsEvent('ocs:view_cart', payload);
    } catch (_) {}
  }

  connectedCallback() {
    super.connectedCallback();
    const { dialog } = this.refs;
    if (dialog) {
      dialog.addEventListener('change', this.#onDialogChange);
      dialog.addEventListener('click', this.#onDialogClick);
      dialog.addEventListener('submit', this.#onDialogSubmit);
      dialog.addEventListener('keydown', this.#onDialogKeydown);
    }

    // Capture phase: stop remove-button clicks from bubbling to DialogComponent's
    // backdrop-close handler. Routing (capture on document) has already fired by this point.
    this.addEventListener('click', this.#preventCloseOnRemove, true);
    // Spinner: show on qty change, clear on cart update
    document.addEventListener('quantity-selector:update', this.#onQtyUpdate);
    document.addEventListener('cart:update', this.#onCartUpdateClearSpinners);
    document.addEventListener('cart:update', this.#onCartUpdateValidateCheckout);
    document.addEventListener('cart:update', this.#onCartUpdateScrollUpsell);
    // Also clear spinners on a failed cart change (e.g. over-inventory 422) so a
    // rejected quantity update doesn't leave the row spinner stuck forever.
    document.addEventListener('cart:error', this.#onCartUpdateClearSpinners);
    // Auto-open on add-to-cart (covers OCS custom paths that dispatch cart:update
    // instead of CartAddEvent which the base class handles for stock forms)
    document.addEventListener('cart:update', this.#onCartUpdateAutoOpen);

    // Server-rendered recommendations are already in this section. Restore the
    // user's collapsed preference before the first drawer paint.
    this.#restoreUpsellPanelState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { dialog } = this.refs;
    if (dialog) {
      dialog.removeEventListener('change', this.#onDialogChange);
      dialog.removeEventListener('click', this.#onDialogClick);
      dialog.removeEventListener('submit', this.#onDialogSubmit);
      dialog.removeEventListener('keydown', this.#onDialogKeydown);
    }
    this.removeEventListener('click', this.#preventCloseOnRemove, true);
    document.removeEventListener('quantity-selector:update', this.#onQtyUpdate);
    document.removeEventListener('cart:update', this.#onCartUpdateClearSpinners);
    document.removeEventListener('cart:update', this.#onCartUpdateValidateCheckout);
    document.removeEventListener('cart:update', this.#onCartUpdateScrollUpsell);
    document.removeEventListener('cart:error', this.#onCartUpdateClearSpinners);
    document.removeEventListener('cart:update', this.#onCartUpdateAutoOpen);
    window.clearTimeout(this.#openingTransitionTimer);
    window.cancelAnimationFrame(this.#initialFocusFrame);
    this.#drawerRefreshToken += 1;
  }

  /** Move initial native <dialog> focus off the first button so Safari does not paint a misleading ring. */
  #focusDialogShellAfterOpen() {
    const { dialog } = this.refs;
    if (!dialog) return;

    window.cancelAnimationFrame(this.#initialFocusFrame);
    this.#initialFocusFrame = window.requestAnimationFrame(() => {
      this.#initialFocusFrame = 0;
      if (dialog.open) dialog.focus({ preventScroll: true });
    });
  }

  /**
   * Stop remove-button clicks from propagating further after routing has run.
   * Without this, DialogComponent.#handleClick sees a detached target (the row
   * is removed from the DOM during animation) and incorrectly closes the drawer.
   */
  #preventCloseOnRemove = (event) => {
    const removeBtn = /** @type {Element} */ (event.target).closest?.('.ocs-cart-item__remove');
    if (removeBtn) {
      event.stopPropagation();
      removeBtn.closest('.ocs-cart-item')?.classList.add('is-loading');
    }
  };

  /** Show loading spinner on the specific item row being updated. */
  #onQtyUpdate = (event) => {
    const cartLine = /** @type {any} */ (event).detail?.cartLine;
    if (!cartLine) return;
    const items = this.querySelectorAll('.ocs-cart-item');
    items[cartLine - 1]?.classList.add('is-loading');
  };

  /**
   * Auto-open the drawer when an item is added via OCS custom paths
   * (ocs-product-card.js PLP cards, upsell) that dispatch cart:update instead
   * of CartAddEvent (which the base CartDrawerComponent already handles).
   */
  #onCartUpdateAutoOpen = (event) => {
    if (!this.hasAttribute('auto-open')) return;
    const source = /** @type {any} */ (event).detail?.data?.source;
    // Only open on genuine add-to-cart, not on qty changes or removes
    if (source === 'product-form-component') {
      this.open();
    }
  };

  /** Clear all spinners once the cart re-renders. */
  #onCartUpdateClearSpinners = (event) => {
    if (event.type === 'cart:update') this.#drawerRefreshToken += 1;
    this.querySelectorAll('.ocs-cart-item.is-loading')
      .forEach((el) => el.classList.remove('is-loading'));
  };

  /** Re-check checkout and interaction state after cart sections have morphed. */
  #onCartUpdateValidateCheckout = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#restoreUpsellPanelState();
        this.#validateCheckout();
      });
    });
  };

  /** Keep upsell adds anchored to the top of the view-bag line list after morphs. */
  #onCartUpdateScrollUpsell = (event) => {
    if (event.detail?.data?.upsellSource) this.#scrollCartLinesToTop();
  };

  /** Handle form control changes (terms checkbox + upsell size auto-submit). */
  #onDialogChange = (event) => {
    // Terms checkbox
    if (event.target.matches('[data-ocs-role="terms-checkbox"]')) {
      this.#persistTerms(event.target.checked);
      this.#validateCheckout();
      return;
    }

    // Multi-variant upsell: selecting a size immediately adds to cart
    if (event.target.matches('[data-ocs-upsell-auto-submit]')) {
      const form = event.target.closest('[data-ocs-upsell-form]');
      if (form) form.requestSubmit();
    }
  };

  /** Sync drawer width state from the current upsell panel. */
  #syncUpsellDialogState() {
    const { dialog } = this.refs;
    if (!dialog) return;

    const panel = this.querySelector('[data-ocs-upsell-panel]');
    if (!panel) {
      dialog.removeAttribute('data-ocs-upsell-state');
      return;
    }

    dialog.setAttribute(
      'data-ocs-upsell-state',
      panel.classList.contains('ocs-upsell-panel--collapsed') ? 'collapsed' : 'expanded'
    );
  }

  /** Prepare width state before Safari paints the native dialog top layer. */
  #prepareDialogForOpen() {
    const { dialog } = this.refs;
    if (!dialog) return;

    dialog.setAttribute('data-ocs-opening', '');
    this.#syncUpsellDialogState();
    requestAnimationFrame(() => this.#syncUpsellDialogState());

    window.clearTimeout(this.#openingTransitionTimer);
    this.#openingTransitionTimer = window.setTimeout(() => {
      dialog.removeAttribute('data-ocs-opening');
    }, 300);
  }

  /** Restore the user's presentation preference after Liquid replaces the upsell markup. */
  #restoreUpsellPanelState() {
    const panel = this.querySelector('[data-ocs-upsell-panel]');
    if (!panel) {
      this.#syncUpsellDialogState();
      return;
    }

    let isCollapsed = false;
    try {
      isCollapsed = sessionStorage.getItem('ocs_upsell_panel_collapsed') === 'true';
    } catch (_) {}

    panel.classList.toggle('ocs-upsell-panel--collapsed', isCollapsed);
    this.querySelectorAll('[data-ocs-upsell-toggle]')
      .forEach((button) => button.setAttribute('aria-expanded', String(!isCollapsed)));
    this.#syncUpsellDialogState();
  }

  /**
   * Refresh the complete drawer section whenever it opens. No cart state is read
   * from existing rows or counters: Shopify's Section Rendering API and Liquid
   * decide the line list, empty state, totals, compliance UI, and recommendations.
   */
  async #refreshDrawerOnOpen() {
    const cartItemsComponent = this.querySelector('cart-items-component[data-section-id]');
    const sectionId = cartItemsComponent?.dataset.sectionId;
    if (!sectionId) return;

    const token = ++this.#drawerRefreshToken;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId.replace(/^shopify-section-/, ''));
      url.searchParams.set('_ocs_cart_drawer', String(Date.now()));
      const response = await fetch(url, { cache: 'no-store', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Section Rendering API returned ${response.status}`);

      const sectionHTML = await response.text();
      if (token !== this.#drawerRefreshToken) return;

      await morphSection(sectionId, sectionHTML);
      this.#restoreUpsellPanelState();
      this.#validateCheckout();
    } catch (error) {
      console.error('[OCS] Cart drawer section refresh failed:', error);
    }
  }

  /** Toggle the upsell panel open/collapsed, persisting state in sessionStorage. */
  #toggleUpsellPanel() {
    const panel = this.querySelector('[data-ocs-upsell-panel]');
    if (!panel) return;
    const isCollapsed = panel.classList.toggle('ocs-upsell-panel--collapsed');
    const expanded = String(!isCollapsed);
    this.querySelectorAll('[data-ocs-upsell-toggle]')
      .forEach((btn) => btn.setAttribute('aria-expanded', expanded));
    this.#syncUpsellDialogState();
    try {
      sessionStorage.setItem('ocs_upsell_panel_collapsed', String(isCollapsed));
    } catch (_) {}
  }

  /** Keyboard activation for upsell size labels (radio inputs are display:none). */
  #onDialogKeydown = (event) => {
    if (event.key === 'Escape') {
      const modal = this.querySelector('[data-ocs-variant-modal]:not([hidden])');
      if (modal) {
        event.stopPropagation();
        this.#closeVariantModal();
        return;
      }
    }
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const label = /** @type {Element} */ (event.target).closest?.('.ocs-upsell-card__size-opt');
    if (label && !label.classList.contains('ocs-upsell-card__size-opt--oos')) {
      event.preventDefault();
      /** @type {HTMLElement} */ (label).click();
    }
  };

  /** Open the variant selection modal for a multi-variant upsell card. */
  #openVariantModal(triggerBtn) {
    const modal = this.querySelector('[data-ocs-variant-modal]');
    if (!modal) return;

    const card = triggerBtn.closest('.ocs-upsell-card--mobile');
    const template = card?.querySelector('[data-ocs-variant-template]');
    if (!template) return;

    // Populate title
    const titleEl = modal.querySelector('[id="ocs-upsell-modal-title"]');
    if (titleEl) titleEl.textContent = triggerBtn.dataset.productTitle || '';

    // Clone variant options into modal
    const optsEl = modal.querySelector('[data-ocs-variant-modal-opts]');
    if (optsEl) {
      optsEl.innerHTML = '';
      optsEl.appendChild(template.content.cloneNode(true));
    }

    modal.removeAttribute('hidden');
    modal.focus?.();
  }

  /** Close the variant selection modal. */
  #closeVariantModal() {
    const modal = this.querySelector('[data-ocs-variant-modal]');
    if (!modal) return;
    modal.setAttribute('hidden', '');
  }

  /** Scroll large carts back to the top of the line-item list after an upsell add. */
  #scrollCartLinesToTop() {
    const scroll = () => {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = prefersReducedMotion ? 'auto' : 'smooth';
      const scrollTargets = [
        this.querySelector('.cart-drawer__content'),
        this.querySelector('.cart-drawer__items'),
      ].filter((target) => target instanceof HTMLElement);

      for (const target of scrollTargets) {
        target.scrollTop = 0;
        target.scrollTo({ top: 0, behavior });
      }
    };

    requestAnimationFrame(() => requestAnimationFrame(scroll));
    window.setTimeout(scroll, 120);
    window.setTimeout(scroll, 360);
  }

  /** Add the selected variant to cart and close the modal. */
  async #addVariantFromModal(optionBtn) {
    const modal = this.querySelector('[data-ocs-variant-modal]');
    const variantId = optionBtn.dataset.variantId;
    if (!variantId) return;

    // Disable all options + show loading state on clicked button
    const allOpts = modal?.querySelectorAll('.ocs-upsell-variant-opt');
    allOpts?.forEach((btn) => { btn.disabled = true; });
    optionBtn.classList.add('is-loading');

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const analytics = prepareUpsellAddAnalytics(formData, optionBtn);

      const sectionIds = getCartSectionIds();
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const response = await fetch(getLocalizedStorefrontPath('cart/add.js'), { method: 'POST', body: formData });
      const data = await response.json();

      if (response.ok) {
        publishUpsellAddAnalytics(analytics);
        const sections = await fetchFreshCartSections(sectionIds, data?.sections || {});
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: data,
              data: {
                source: 'product-form-component',
                itemCount: getFormDataQuantity(formData),
                variantId,
                upsellSource: true,
                sections,
              },
            },
          })
        );
        this.#closeVariantModal();
        this.#scrollCartLinesToTop();
      }
    } catch (error) {
      console.error('[OCS] Variant modal add error:', error);
    } finally {
      optionBtn.classList.remove('is-loading');
      allOpts?.forEach((btn) => { btn.disabled = btn.classList.contains('ocs-upsell-variant-opt--oos'); });
    }
  }

  /** Handle button clicks (checkout + upsell toggle + remove GA4). */
  #onDialogClick = (event) => {
    if (event.target.closest('[data-ocs-upsell-toggle]')) {
      this.#toggleUpsellPanel();
      return;
    }

    const mobileToggle = event.target.closest('[data-ocs-upsell-mobile-toggle]');
    if (mobileToggle) {
      const panel = mobileToggle.closest('[data-ocs-upsell-mobile]');
      const products = panel?.querySelector('#ocs-upsell-mobile-products');
      if (!panel || !products) return;
      const isOpen = !products.hidden;
      products.hidden = isOpen;
      mobileToggle.setAttribute('aria-expanded', String(!isOpen));
      if (isOpen) panel.removeAttribute('data-open');
      else panel.setAttribute('data-open', '');
      return;
    }

    // Variant modal: open on trigger button click
    const variantBtn = event.target.closest('[data-ocs-upsell-variant-btn]');
    if (variantBtn) {
      this.#openVariantModal(variantBtn);
      return;
    }

    // Variant modal: close on backdrop or close button
    if (event.target.closest('[data-ocs-variant-modal-close]')) {
      this.#closeVariantModal();
      return;
    }

    // Variant modal: add item on option click
    const variantOpt = event.target.closest('.ocs-upsell-variant-opt:not([disabled])');
    if (variantOpt) {
      this.#addVariantFromModal(variantOpt);
      return;
    }

    const checkoutBtn = event.target.closest('[data-ocs-role="checkout-button"]');
    if (checkoutBtn && !checkoutBtn.disabled) {
      this.#handleCheckout(checkoutBtn);
      return;
    }

    // const removeBtn = event.target.closest('.ocs-cart-item__remove');
    // if (removeBtn) {
    //   const { gtmProductTitle, gtmVendor, gtmVariantId, gtmPrice, gtmProductTags } = removeBtn.dataset;
    //   window.dataLayer = window.dataLayer || [];
    //   window.dataLayer.push({
    //     event: 'remove_from_cart',
    //     item_name: gtmProductTitle || '',
    //     item_brand: gtmVendor || '',
    //     item_id: String(gtmVariantId || ''),
    //     price: parseInt(gtmPrice || '0', 10) / 100,
    //     quantity: 1,
    //   });
    // }
  };

  /**
   * Handle AJAX add-to-cart for upsell forms ([data-ocs-upsell-form]).
   * Appends GA4 line-item properties and fires with source 'product-form-component'
   * so the toast notification fires consistently.
   */
  #onDialogSubmit = async (event) => {
    const form = event.target.closest('[data-ocs-upsell-form]');
    if (!form) return;
    event.preventDefault();

    const btn = form.querySelector('[type="submit"]');
    const overlay = form.closest('.ocs-upsell-card__overlay');
    const isMobileForm = !overlay && form.classList.contains('ocs-upsell-card--mobile__form');
    if (btn) btn.disabled = true;
    if (overlay) overlay.classList.add('is-loading');
    if (isMobileForm) form.classList.add('is-loading');

    try {
      const formData = new FormData(form);
      const variantId = formData.get('id');

      // Guard: multi-variant form submitted without picking a size
      if (!variantId) {
        if (btn) btn.disabled = false;
        if (overlay) overlay.classList.remove('is-loading');
        if (isMobileForm) form.classList.remove('is-loading');
        return;
      }

      const selectedControl = form.querySelector('[name="id"]:checked') || form.querySelector('[name="id"]');
      const analytics = prepareUpsellAddAnalytics(formData, selectedControl);

      const sectionIds = getCartSectionIds();
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const response = await fetch(getLocalizedStorefrontPath('cart/add.js'), { method: 'POST', body: formData });
      const data = await response.json();

      if (response.ok) {
        publishUpsellAddAnalytics(analytics);
        const sections = await fetchFreshCartSections(sectionIds, data?.sections || {});
        // // GA4 add_to_cart event for upsell adds
        // window.dataLayer = window.dataLayer || [];
        // window.dataLayer.push({
        //   event: 'add_to_cart',
        //   location: 'Cart Drawer',
        //   item_name: data.product_title || '',
        //   item_brand: data.vendor || '',
        //   item_id: String(variantId),
        //   price: (data.price || 0) / 100,
        //   quantity: data.quantity || 1,
        //   ...ga4Props,
        // });

        // Use 'product-form-component' so the toast fires (per business spec:
        // "When a user adds an item to their cart, a toast pop-up will appear")
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: data,
              data: {
                source: 'product-form-component',
                itemCount: getFormDataQuantity(formData),
                variantId: String(variantId),
                upsellSource: true,
                sections,
              },
            },
          })
        );
        this.#scrollCartLinesToTop();
      }
    } catch (error) {
      console.error('[OCS] Upsell add error:', error);
    } finally {
      if (btn) btn.disabled = false;
      if (overlay) overlay.classList.remove('is-loading');
      if (isMobileForm) form.classList.remove('is-loading');
    }
  };

  /**
   * Enable/disable the checkout button based on:
   * 1. Terms checkbox checked
   * 2. Not over the 30g limit
   * 3. Cart is not empty
   */
  #validateCheckout() {
    const { dialog } = this.refs;
    if (!dialog) return;

    const termsCheckbox = dialog.querySelector('[data-ocs-role="terms-checkbox"]');
    const checkoutButton = dialog.querySelector('[data-ocs-role="checkout-button"]');
    if (!termsCheckbox || !checkoutButton) return;

    const isOverLimit = checkoutButton.dataset.ocsOverLimit === 'true';
    const isCartEmpty = checkoutButton.dataset.ocsCartEmpty === 'true';
    const termsAccepted = termsCheckbox.checked;

    checkoutButton.disabled = !termsAccepted || isOverLimit || isCartEmpty;
  }

  /**
   * Persist terms acceptance as cart attribute `va` so it survives morphSection
   * re-renders (Liquid reads cart.attributes.va to set checked state).
   * @param {boolean} checked
   */
  async #persistTerms(checked) {
    try {
      await fetch(getLocalizedStorefrontPath('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { va: checked ? 'true' : 'false' } }),
      });
    } catch (_) {
      // Non-critical — validation still works off the DOM checkbox state
    }
  }

  /**
   * Persist checkout attributes, then redirect to checkout.
   *
   * `dried_cannabis_equiv` uses the rendered drawer value so the purchase pixel
   * can read the same cannabis equivalent that the shopper saw before checkout.
   * @param {HTMLButtonElement} button
   */
  async #handleCheckout(button) {
    button.disabled = true;
    try {
      const driedCannabisEquiv = readCartViewSnapshot(this).driedCannabisEquiv.trim();
      await fetch(getLocalizedStorefrontPath('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            va: 'true',
            dried_cannabis_equiv: driedCannabisEquiv,
          },
        }),
      });
      window.location.href = getLocalizedStorefrontPath('checkout');
    } catch (error) {
      console.error('[OCS] Checkout error:', error);
      // Re-enable so user can retry
      this.#validateCheckout();
    }
  }

}

if (!customElements.get('ocs-cart-drawer-component')) {
  customElements.define('ocs-cart-drawer-component', OcsCartDrawerComponent);
}
